/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { canPlayNativeHls, loadWithHls } from "./hls-loader.js";
import { type ProgressContext, type ProgressHandlers, restoreProgress, scheduleProgressSave } from "./progress.js";
import { capturePlayerError, trackBufferHealth } from "./sentry-integration.js";
import {
  type TelemetryContext,
  type TelemetryHandler,
  createTelemetryContext,
  trackPlaybackEvent,
  trackVideoAbandon,
  trackVideoComplete,
  trackVideoPause,
  trackVideoResume,
  trackVideoSeek,
  trackVideoStart,
} from "./telemetry-helpers.js";
import { refreshToken, startTokenRefreshInterval, stopTokenRefreshInterval } from "./token-refresh.js";

/** HLS media source and optional subtitles. */
export type Source = { hlsUrl: string; subtitles?: Array<{ lang: string; url: string; forced?: boolean }> };

// Re-export progress types for external use
export type { ProgressContext, ProgressHandlers } from "./progress.js";

// Re-export telemetry types for external use
export type { TelemetryContext, TelemetryHandler } from "./telemetry-helpers.js";

// Re-export Sentry integration types for external use
export {
  capturePlayerError,
  capturePlayerMetrics,
  handleHlsError,
  setPlayerSentryContext,
  trackBufferHealth,
} from "./sentry-integration.js";
export type {
  HlsError,
  PlayerErrorContext,
  PlayerErrorType,
  PlayerMetrics,
  PlayerSentryConfig,
} from "./sentry-integration.js";

/** Event handler types emitted by the controller. */
export type PlayerEvents = {
  timeupdate: (currentTime: number, duration: number) => void;
  error: (err: unknown) => void;
};

/** Token refresh handler - fetches fresh signed URL for a title. */
export type TokenRefreshHandler = (titleId: string) => Promise<{
  hlsUrl: string;
  subtitles?: Array<{ lang: string; url: string; forced?: boolean }>;
}>;
/** Video quality level. -1 or "auto" means automatic selection. */
export type Level = {
  id: number; // -1 represents Auto
  label: string;
  bitrate?: number;
  width?: number;
  height?: number;
  isAuto?: boolean;
};

/** Caption/subtitle track information. */
export type CaptionTrack = { id: number; lang: string; label?: string; forced?: boolean; url?: string };

/** Subtitle vertical positioning options. */
export type SubtitlePosition = "auto" | "top" | "middle" | "bottom";

/** Subtitle font size options. */
export type FontSize = "small" | "medium" | "large" | "x-large";

/** Subtitle background opacity (0-100%). */
export type BackgroundOpacity = 0 | 25 | 50 | 75 | 100;

/** Subtitle text color presets. */
export type TextColor = "white" | "yellow" | "gray" | "black";

/** Subtitle font family options. */
export type FontFamily = "proportional-sans" | "monospace-sans" | "proportional-serif" | "monospace-serif";

/** Subtitle text edge/shadow style. */
export type TextEdgeStyle = "none" | "drop-shadow" | "uniform" | "drop-shadow-strong";

/** Public controller API used by UI layers. */
export interface PlayerController {
  /** Provide the HTMLVideoElement to control. Must be called before load(). */
  attach(video: HTMLVideoElement): void;
  /** Load an HLS source. Chooses native or hls.js automatically. */
  load(source: Source): Promise<void>;
  play(): Promise<void>;
  pause(): Promise<void>;
  seek(seconds: number): void;
  destroy(): void;

  onTimeupdate(handler: (currentTime: number, duration: number) => void): void;
  onError(handler: (err: unknown) => void): void;

  // Quality selection
  getLevels(): ReadonlyArray<Level>;
  setLevel(id: number | "auto"): void;
  onLevelsUpdated(handler: (levels: ReadonlyArray<Level>, selected: number | "auto") => void): void;

  // Captions
  getCaptions(): ReadonlyArray<CaptionTrack>;
  setCaption(id: number | "off"): void;
  onCaptionChange(handler: (active: number | "off") => void): void;
  onCaptionsUpdated(handler: (captions: ReadonlyArray<CaptionTrack>) => void): void;
  /** Internal method to clear caption redundancy guard - used for restoration timing */
  _clearCaptionGuard(): void;

  // Playback speed
  getPlaybackRate(): number;
  setPlaybackRate(rate: number): void;
  onPlaybackRateChange(handler: (rate: number) => void): void;

  // Progress autosave/restore
  /** Configure progress handlers for save/restore functionality. */
  setProgressHandlers(handlers: ProgressHandlers): void;
  /** Set asset ID for progress tracking. Call before load() to enable progress restore. */
  setAssetId(assetId: string): void;

  // Token refresh for signed URLs
  /** Set title ID for token refresh. Required if using signed URLs. */
  setTitleId(titleId: string): void;
  /** Configure token refresh handler. Enables automatic token refresh before expiration. */
  setTokenRefreshHandler(handler: TokenRefreshHandler): void;

  // Telemetry tracking
  /** Configure telemetry handler for tracking player events. */
  setTelemetryHandler(handler: TelemetryHandler): void;

  // Error tracking
  /** Configure Sentry context for error tracking. */
  setSentryContext(config: {
    assetId?: string;
    titleId?: string;
    userId?: string;
    tags?: Record<string, string>;
  }): void;

  // Caption positioning
  /** Set control visibility to dynamically position captions above/below controls. */
  setControlsVisible(visible: boolean): void;

  // Subtitle positioning
  /** Get current subtitle position setting. */
  getSubtitlePosition(): SubtitlePosition;
  /** Set subtitle vertical position (top/middle/bottom/auto). */
  setSubtitlePosition(position: SubtitlePosition): void;
  /** Listen for subtitle position changes. */
  onSubtitlePositionChange(handler: (position: SubtitlePosition) => void): void;
}

/** Internal event handler registry type */
export type Handlers = { timeupdate: Array<(t: number, d: number) => void>; error: Array<(e: unknown) => void> };

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

export type ControllerContext = {
  handlers: Handlers;
  video: HTMLVideoElement | null;
  hls: { destroy(): void } | null;
  lastEmit: number;
  // Quality selection
  levels: Level[];
  selectedLevel: number | "auto";
  levelHandlers: Array<(levels: ReadonlyArray<Level>, selected: number | "auto") => void>;
  // Captions
  captions: CaptionTrack[];
  activeCaption: number | "off";
  captionHandlers: Array<(active: number | "off") => void>;
  // Handlers for when the list of available caption tracks updates
  captionListHandlers: Array<(captions: ReadonlyArray<CaptionTrack>) => void>;
  // Playback speed
  playbackRate: number;
  playbackRateHandlers: Array<(rate: number) => void>;
  // Subtitle positioning
  subtitlePosition: SubtitlePosition;
  subtitlePositionHandlers: Array<(position: SubtitlePosition) => void>;
  // Track if using native HLS (for Safari HLS bug workaround)
  isNativeHls: boolean;
  // DOM event refs
  timeupdateHandler: (() => void) | null;
  // Progress autosave/restore - using modular ProgressContext
  progress: ProgressContext;
  // Token refresh for signed URLs
  titleId: string | null;
  tokenRefreshHandler: TokenRefreshHandler | null;
  tokenRefreshInterval: NodeJS.Timeout | null;
  // Telemetry tracking
  telemetry: TelemetryContext;
  isRefreshing: boolean;
};

function emitTimeupdate(ctx: ControllerContext, t: number, d: number): void {
  for (const h of ctx.handlers.timeupdate) h(t, d);
}

export function emitError(ctx: ControllerContext, e: unknown): void {
  // Capture error in Sentry if it's a significant error
  if (e instanceof Error) {
    capturePlayerError(e, {
      errorType: "playback_error",
      assetId: ctx.progress.assetId || undefined,
      titleId: ctx.titleId || undefined,
      position: ctx.video?.currentTime,
      duration: ctx.video?.duration,
      quality: ctx.selectedLevel,
      networkState: ctx.video?.networkState,
      readyState: ctx.video?.readyState,
    });
  }

  for (const h of ctx.handlers.error) h(e);
}

export function emitLevels(ctx: ControllerContext): void {
  for (const h of ctx.levelHandlers) h(ctx.levels, ctx.selectedLevel);
}

export function emitCaption(ctx: ControllerContext): void {
  for (const h of ctx.captionHandlers) h(ctx.activeCaption);
}

export function emitCaptions(ctx: ControllerContext): void {
  for (const h of ctx.captionListHandlers) h(ctx.captions);
}

function emitPlaybackRate(ctx: ControllerContext): void {
  for (const h of ctx.playbackRateHandlers) h(ctx.playbackRate);
}

function onVideoTimeupdate(ctx: ControllerContext): void {
  if (!ctx.video) return;
  const now = Date.now();
  if (now - ctx.lastEmit < 800) return; // throttle
  ctx.lastEmit = now;
  const d = Number.isFinite(ctx.video.duration) ? ctx.video.duration : 0;
  const t = ctx.video.currentTime || 0;
  emitTimeupdate(ctx, t, d);

  // Track video completion (when reaching 95% or end)
  if (ctx.telemetry.hasStarted && d > 0 && (t >= d - 1 || t / d >= 0.95)) {
    if (!ctx.video.ended && ctx.telemetry.lastPosition < d * 0.95) {
      // First time reaching completion threshold
      trackVideoComplete(ctx.telemetry, d);
    }
  }

  // Update telemetry position
  if (ctx.telemetry.hasStarted) {
    ctx.telemetry.lastPosition = t;
  }

  // Track buffer health every 5 seconds for performance monitoring
  if (now % 5000 < 1000) {
    // roughly every 5 seconds
    trackBufferHealth(ctx.video, {
      assetId: ctx.progress.assetId || undefined,
      titleId: ctx.titleId || undefined,
    });
  }

  // Progress autosave using modular function
  scheduleProgressSave(ctx.progress, t, d, (error: string) => {
    emitError(ctx, error);
  });
}

function detachListeners(ctx: ControllerContext): void {
  if (!ctx.video || !ctx.timeupdateHandler) return;
  ctx.video.removeEventListener("timeupdate", ctx.timeupdateHandler);
  ctx.timeupdateHandler = null;
}

function initializeCaptionsFromSource(ctx: ControllerContext, source: Source): void {
  ctx.captions.splice(
    0,
    ctx.captions.length,
    ...(source.subtitles ?? []).map((s, i) => ({ id: i, lang: s.lang, label: s.lang, forced: s.forced })),
  );
  ctx.activeCaption = "off";
  emitCaption(ctx);
  emitCaptions(ctx);
}

function applyLevelToHls(ctx: ControllerContext): void {
  try {
    const anyHls = ctx.hls as unknown as { loadLevel?: number; currentLevel?: number } | null;
    if (anyHls && "loadLevel" in anyHls) {
      // HLS.js quality API: loadLevel is writable, currentLevel is read-only
      // -1 = auto quality selection, 0+ = specific quality level
      if (ctx.selectedLevel === "auto") {
        anyHls.loadLevel = -1;
      } else if (typeof ctx.selectedLevel === "number") {
        anyHls.loadLevel = ctx.selectedLevel;
      }
    }
  } catch {
    // ignore
  }
}

function applyCaptionToPlayer(ctx: ControllerContext, captionId: number | "off"): void {
  try {
    // Guard: Skip if we're already applying the same caption
    if ((ctx as { lastAppliedCaption?: number | "off" }).lastAppliedCaption === captionId) {
      return;
    }
    (ctx as { lastAppliedCaption?: number | "off" }).lastAppliedCaption = captionId;

    // When using renderTextTracksNatively: true in HLS.js (which we are),
    // subtitles are controlled via video.textTracks, not hls.subtitleTrack
    const tracks = (
      ctx.video as unknown as {
        textTracks?: { length: number; [i: number]: { language?: string; mode?: string; kind?: string } };
      }
    ).textTracks;

    if (!tracks || typeof tracks.length !== "number") {
      console.warn("⚠️ No textTracks available on video element");
      return;
    }

    // Iterate through all text tracks
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i] as { language?: string; mode?: string; kind?: string };

      // Skip track 0 (dummy accessibility track from <video><track /></video>)
      if (i === 0) {
        continue;
      }

      // Only process subtitle/caption tracks
      const isSubtitleTrack = track.kind === "subtitles" || track.kind === "captions";
      if (!isSubtitleTrack) {
        continue;
      }

      // HLS.js adds tracks sequentially after the dummy track at index 0
      const hlsTrackIndex = i - 1; // Adjust for dummy track
      const shouldShow = captionId !== "off" && hlsTrackIndex === captionId;

      // Use "hidden" instead of "showing" so cues are loaded but not rendered natively
      // This allows CustomSubtitleOverlay to access the cues while preventing native rendering
      track.mode = shouldShow ? "hidden" : "disabled";
    }
  } catch (err) {
    // Log error for debugging but don't crash
    console.error("Failed to apply caption:", err);
  }
}

function resetTelemetryForNewSource(ctx: ControllerContext, video: HTMLVideoElement): void {
  // Track video abandon if switching sources
  if (ctx.telemetry.hasStarted && video.duration) {
    trackVideoAbandon(ctx.telemetry, video.duration);
  }

  // Reset telemetry state for new source
  ctx.telemetry.hasStarted = false;
  ctx.telemetry.lastPosition = 0;
  ctx.telemetry.assetId = ctx.progress.assetId || undefined;
}

function cleanupPreviousHls(ctx: ControllerContext): void {
  if (ctx.hls) {
    try {
      ctx.hls.destroy();
    } catch {
      // ignore
    }
    ctx.hls = null;
  }
}

function loadNativeHls(ctx: ControllerContext, video: HTMLVideoElement, source: Source): void {
  initializeCaptionsFromSource(ctx, source);
  try {
    (video as unknown as { src?: string }).src = source.hlsUrl;
    (video as unknown as { load?: () => void }).load?.();
  } catch (e) {
    emitError(ctx, e);
  }
}

function setupHlsErrorHandler(ctx: ControllerContext): void {
  if (!ctx.hls) return;
  const hlsInstance = ctx.hls as unknown as {
    on?: (evt: string, cb: (evt: string, data: { response?: { code?: number } }) => void) => void;
  };
  if (typeof hlsInstance.on === "function") {
    hlsInstance.on("hlsError", (_evt: string, data: { response?: { code?: number } }) => {
      if (data.response?.code === 401) {
        void refreshToken(ctx);
      }
    });
  }
}

function handleCaptionRestoration(ctx: ControllerContext, captions: CaptionTrack[]): void {
  const previousSelection = ctx.activeCaption;
  ctx.captions.splice(0, ctx.captions.length, ...captions);

  const shouldRestore =
    previousSelection !== "off" && typeof previousSelection === "number" && previousSelection < captions.length;

  if (shouldRestore) {
    delete (ctx as { lastAppliedCaption?: number | "off" }).lastAppliedCaption;
    applyCaptionToPlayer(ctx, previousSelection);
    ctx.activeCaption = previousSelection;
  } else {
    ctx.activeCaption = "off";
  }

  emitCaption(ctx);
  emitCaptions(ctx);
}

function makeAttach(ctx: ControllerContext) {
  return (v: HTMLVideoElement) => {
    if (ctx.video && ctx.video !== v) {
      detachListeners(ctx);
    }
    ctx.video = v;
    ctx.lastEmit = 0;
    ctx.timeupdateHandler = () => onVideoTimeupdate(ctx);
    ctx.video.addEventListener("timeupdate", ctx.timeupdateHandler);
  };
}

function makeLoad(ctx: ControllerContext) {
  return async (source: Source) => {
    if (!ctx.video) throw new Error("PlayerController.attach(video) must be called before load()");

    resetTelemetryForNewSource(ctx, ctx.video);
    cleanupPreviousHls(ctx);

    const canNative = canPlayNativeHls(ctx.video);
    ctx.isNativeHls = canNative;

    if (canNative) {
      loadNativeHls(ctx, ctx.video, source);
    } else {
      // Callbacks to populate levels and captions from HLS manifest
      const onLevelsLoaded = (levels: Level[]) => {
        ctx.levels.splice(0, ctx.levels.length, ...levels);
        emitLevels(ctx);
      };

      ctx.hls = await loadWithHls(ctx, ctx.video, source.hlsUrl, onLevelsLoaded, (captions) =>
        handleCaptionRestoration(ctx, captions),
      );

      // Fallback: if HLS.js didn't detect captions, use source.subtitles
      if (ctx.captions.length === 0 && source.subtitles && source.subtitles.length > 0) {
        initializeCaptionsFromSource(ctx, source);
      }

      setupHlsErrorHandler(ctx);
    }
    const d = Number.isFinite(ctx.video.duration) ? ctx.video.duration : 0;
    emitTimeupdate(ctx, 0, d);
    emitLevels(ctx);

    // DISABLED: setupCaptionPositioning - not needed when using CustomSubtitleOverlay
    // CustomSubtitleOverlay handles positioning via CSS, and setupCaptionPositioning
    // mutates cue.line values that CustomSubtitleOverlay reads, causing positioning conflicts
    // setupCaptionPositioning(ctx);

    // Restore progress if available
    await restoreProgress(
      ctx.progress,
      ctx.video,
      (t, d) => emitTimeupdate(ctx, t, d),
      (err) => emitError(ctx, err),
    );

    // Start token refresh interval if configured
    startTokenRefreshInterval(ctx);
  };
}

function makePlay(ctx: ControllerContext) {
  return () => {
    if (!ctx.video) return Promise.resolve();

    // Track video start or resume
    const currentTime = ctx.video.currentTime || 0;
    const assetId = ctx.progress.assetId;

    if (assetId && !ctx.telemetry.hasStarted) {
      // First play - track video start
      trackVideoStart(ctx.telemetry, assetId, {
        duration: ctx.video.duration || 0,
        quality: ctx.selectedLevel,
        hasSubtitles: ctx.captions.length > 0,
      });
    } else if (assetId && ctx.telemetry.hasStarted) {
      // Resume from pause
      trackVideoResume(ctx.telemetry, currentTime);
    }

    try {
      const maybePromise = (ctx.video as unknown as { play?: () => Promise<void> }).play?.();
      return maybePromise ?? Promise.resolve();
    } catch {
      return Promise.resolve();
    }
  };
}

function makePause(ctx: ControllerContext) {
  return () => {
    if (!ctx.video) return Promise.resolve();

    // Track video pause
    const currentTime = ctx.video.currentTime || 0;
    if (ctx.telemetry.hasStarted) {
      trackVideoPause(ctx.telemetry, currentTime);
    }

    try {
      (ctx.video as unknown as { pause?: () => void }).pause?.();
    } catch {
      // ignore
    }
    return Promise.resolve();
  };
}

function makeSeek(ctx: ControllerContext) {
  return (seconds: number) => {
    if (!ctx.video) return;

    // Track seek event
    const fromPosition = ctx.video.currentTime || 0;
    const duration = Number.isFinite(ctx.video.duration) ? ctx.video.duration : Math.max(0, seconds);
    const next = clamp(seconds, 0, duration || Number.MAX_SAFE_INTEGER);

    if (ctx.telemetry.hasStarted && Math.abs(next - fromPosition) > 1) {
      // Only track significant seeks (> 1 second difference)
      trackVideoSeek(ctx.telemetry, fromPosition, next);
    }

    try {
      ctx.video.currentTime = next;
    } catch {
      // ignore
    }
    emitTimeupdate(ctx, next, Number.isFinite(ctx.video.duration) ? ctx.video.duration : duration);
  };
}

function makeDestroy(ctx: ControllerContext) {
  return () => {
    // Track video abandon if session was active
    if (ctx.telemetry.hasStarted && ctx.video?.duration) {
      trackVideoAbandon(ctx.telemetry, ctx.video.duration);
    }

    // Clean up caption positioning listeners
    const cleanup = (ctx as { captionCleanup?: () => void }).captionCleanup;
    if (cleanup) {
      cleanup();
    }

    detachListeners(ctx);
    stopTokenRefreshInterval(ctx);
    if (ctx.hls) {
      try {
        ctx.hls.destroy();
      } catch {
        // ignore
      }
      ctx.hls = null;
    }
    ctx.video = null;
  };
}

/**
 * Factory: creates a new PlayerController bound to a provided video element via attach().
 * The function is intentionally small; behavior is delegated to small helpers above.
 */
export function createPlayerController(): PlayerController {
  const ctx: ControllerContext = {
    handlers: { timeupdate: [], error: [] },
    video: null,
    hls: null,
    lastEmit: 0,
    levels: [{ id: -1, label: "Auto", isAuto: true }],
    selectedLevel: "auto",
    captions: [],
    activeCaption: "off",
    captionListHandlers: [],
    playbackRate: 1,
    playbackRateHandlers: [],
    subtitlePosition: "auto",
    subtitlePositionHandlers: [],
    isNativeHls: false,
    levelHandlers: [],
    captionHandlers: [],
    timeupdateHandler: null,
    // Progress context - initialized empty
    progress: {
      progressHandlers: null,
      assetId: null,
      lastProgressSave: 0,
      pendingProgressSave: null,
    },
    // Token refresh context - initialized empty
    titleId: null,
    tokenRefreshHandler: null,
    tokenRefreshInterval: null,
    isRefreshing: false,
    // Telemetry context - initialized empty
    telemetry: createTelemetryContext(),
  };

  return {
    attach: makeAttach(ctx),
    load: makeLoad(ctx),
    play: makePlay(ctx),
    pause: makePause(ctx),
    seek: makeSeek(ctx),
    destroy: makeDestroy(ctx),
    onTimeupdate(handler) {
      ctx.handlers.timeupdate.push(handler);
    },
    onError(handler) {
      ctx.handlers.error.push(handler);
    },
    getLevels() {
      return ctx.levels;
    },
    setLevel(id) {
      const oldLevel = ctx.selectedLevel;
      ctx.selectedLevel = id === "auto" || id === -1 ? "auto" : id;
      applyLevelToHls(ctx);
      emitLevels(ctx);

      // Track quality change
      if (ctx.telemetry.hasStarted && oldLevel !== ctx.selectedLevel) {
        trackPlaybackEvent(ctx.telemetry, "quality_change", {
          from: oldLevel,
          to: ctx.selectedLevel,
          position: ctx.video?.currentTime || 0,
        });
      }
    },
    onLevelsUpdated(handler) {
      ctx.levelHandlers.push(handler);
    },
    getCaptions() {
      return ctx.captions;
    },
    setCaption(id) {
      const oldCaption = ctx.activeCaption;
      const next = id === "off" ? "off" : id;
      ctx.activeCaption = next;

      // Apply caption selection to player
      applyCaptionToPlayer(ctx, next);
      emitCaption(ctx);

      // Track caption change
      if (ctx.telemetry.hasStarted && oldCaption !== next) {
        const captionLabel = next === "off" ? "off" : ctx.captions[next]?.lang || `track_${next}`;
        trackPlaybackEvent(ctx.telemetry, "caption_change", {
          from: oldCaption === "off" ? "off" : ctx.captions[oldCaption]?.lang || `track_${oldCaption}`,
          to: captionLabel,
          position: ctx.video?.currentTime || 0,
        });
      }
    },
    onCaptionChange(handler) {
      ctx.captionHandlers.push(handler);
    },
    onCaptionsUpdated(handler) {
      ctx.captionListHandlers.push(handler);
    },
    _clearCaptionGuard() {
      // Clear the redundancy guard to allow reapplication of same caption ID
      // Used when tracks are loaded asynchronously and need to be applied to new TextTrack objects
      delete (ctx as { lastAppliedCaption?: number | "off" }).lastAppliedCaption;
    },
    getPlaybackRate() {
      return ctx.playbackRate;
    },
    setPlaybackRate(rate) {
      if (!ctx.video) return;
      const clampedRate = Math.max(0.25, Math.min(2, rate));
      ctx.playbackRate = clampedRate;
      try {
        ctx.video.playbackRate = clampedRate;
      } catch {
        // ignore
      }
      emitPlaybackRate(ctx);

      // Track playback speed change
      if (ctx.telemetry.hasStarted) {
        trackPlaybackEvent(ctx.telemetry, "speed_change", {
          speed: clampedRate,
          position: ctx.video.currentTime || 0,
        });
      }
    },
    onPlaybackRateChange(handler) {
      ctx.playbackRateHandlers.push(handler);
    },
    // Progress methods
    setProgressHandlers(handlers) {
      ctx.progress.progressHandlers = handlers;
    },
    setAssetId(assetId) {
      ctx.progress.assetId = assetId;
    },
    // Token refresh methods
    setTitleId(titleId) {
      ctx.titleId = titleId;
    },
    setTokenRefreshHandler(handler) {
      ctx.tokenRefreshHandler = handler;
    },
    // Telemetry methods
    setTelemetryHandler(handler) {
      ctx.telemetry.handler = handler;
    },
    // Error tracking methods
    setSentryContext(config) {
      // Dynamically import to avoid circular dependencies
      import("./sentry-integration.js")
        .then(({ setPlayerSentryContext }) => {
          setPlayerSentryContext(config);
        })
        .catch(() => {
          // Sentry integration not available - silently continue
        });
    },
    // Caption positioning method
    setControlsVisible(visible) {
      const setter = (ctx as { setControlsVisible?: (visible: boolean) => void }).setControlsVisible;
      if (setter) {
        setter(visible);
      }
    },
    // Subtitle positioning methods
    getSubtitlePosition() {
      return ctx.subtitlePosition;
    },
    setSubtitlePosition(position) {
      ctx.subtitlePosition = position;
      // Trigger repositioning of active cues
      const updater = (ctx as { updateSubtitlePosition?: () => void }).updateSubtitlePosition;
      if (updater) {
        updater();
      }
      // Emit change event
      for (const handler of ctx.subtitlePositionHandlers) {
        handler(position);
      }
    },
    onSubtitlePositionChange(handler) {
      ctx.subtitlePositionHandlers.push(handler);
    },
  };
}

/**
 * Generate Cloudflare Stream on-demand thumbnail URL for timeline scrubbing
 *
 * @param streamVideoId - Cloudflare Stream video UID
 * @param timeSeconds - Time position in seconds
 * @param options - Optional width, height, and format (jpg, gif)
 * @returns Thumbnail URL
 *
 * @example
 * // Static thumbnail at 2m30s
 * getStreamThumbnail('abc123', 150)
 * // => 'https://videodelivery.net/abc123/thumbnails/thumbnail.jpg?time=150s'
 *
 * // Animated GIF thumbnail (3s duration)
 * getStreamThumbnail('abc123', 150, { format: 'gif', duration: 3 })
 * // => 'https://videodelivery.net/abc123/thumbnails/thumbnail.gif?time=150s&duration=3s'
 */
export function getStreamThumbnail(
  streamVideoId: string,
  timeSeconds: number,
  options: { width?: number; height?: number; format?: "jpg" | "gif"; duration?: number } = {},
): string {
  const { width, height, format = "jpg", duration } = options;
  const baseUrl = `https://videodelivery.net/${streamVideoId}/thumbnails/thumbnail.${format}`;

  const params = new URLSearchParams();
  params.append("time", `${Math.floor(timeSeconds)}s`);

  if (width) params.append("width", width.toString());
  if (height) params.append("height", height.toString());
  if (format === "gif" && duration) params.append("duration", `${duration}s`);

  return `${baseUrl}?${params.toString()}`;
}
